import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import DOMPurify from "dompurify";
import toast from "react-hot-toast";
import QRCode from "qrcode";
import { FiActivity, FiArrowLeft, FiArrowUpRight, FiBarChart2, FiCalendar, FiCheck, FiCopy, FiDownload, FiEdit2, FiMaximize2, FiMousePointer, FiTrash2, FiUser } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import SideBar from "../../components/sidebar/Sidebar";
import EditShortenDetails from "../Admin/Modal/EditShorten/EditShortenDetails";
import EditClientDetails from '../Admin/Modal/EditClient/EditClientDetails'
import { useUpdateClientDetailsHook } from "../Admin/Modal/EditClient/hook";
import ClickStats from "./ClickStats";
import UserLoginData from "../Auth/UserLoginData";
import QrCodeModal from "./QrCodeModal";



export const ViewShortUrl = () => {

  const shortCode = decodeURIComponent(window.location.pathname.replace("/list/view/", ""));
  const { detailsOfUrl, setUrlDetails, deleteUrlDetails } = useUpdateClientDetailsHook(shortCode);

  const navigate = useNavigate();

  const redirectTo = () => {
    navigate("/list");
  };

  const [copiedUrl, setCopiedUrl] = useState("");
  const [openDescriptionModal, setOpenDescriptionModal] = useState(false);
  const description = detailsOfUrl?.description || "No description provided";
  const sanitizedDescription = DOMPurify.sanitize(description, {
    ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "u", "s", "ul", "ol", "li", "blockquote", "h1", "h2", "h3", "h4", "h5", "h6", "div", "span", "a"],
    ALLOWED_ATTR: ["href", "title"],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false
  });
  const descriptionDocument = new DOMParser().parseFromString(sanitizedDescription, "text/html");
  descriptionDocument.querySelectorAll("p, br, li, blockquote, h1, h2, h3, h4, h5, h6, div").forEach(element => {
    element.append(descriptionDocument.createTextNode(" "));
  });
  const descriptionText = descriptionDocument.body.textContent.replace(/\s+/g, " ").trim() || "No description provided";
  const descriptionCharacters = Array.from(descriptionText);
  const hasLongDescription = descriptionCharacters.length > 128;
  const descriptionPreview = hasLongDescription ? descriptionCharacters.slice(0, 128).join("") : descriptionText;
  const shortUrl = `${window.location.origin}/${detailsOfUrl?.short_code || shortCode}`;

  const [qrDataUrl, setQrDataUrl] = useState("");

  useEffect(() => {
    let isMounted = true;
    if (!shortUrl) return;

    QRCode.toDataURL(shortUrl, {
      width: 200,
      margin: 1,
      color: {
        dark: "#0f172a",
        light: "#ffffff",
      },
    })
      .then((url) => {
        if (isMounted) setQrDataUrl(url);
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [shortUrl]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shortUrl);
      setCopiedUrl(shortUrl);
      toast.success("Short link copied");
    } catch {
      toast.error("Could not copy the short link.");
    }
  };

  const [openEditModel, setOpenEditModel] = useState(false);

  const handleOpenModel = () => {
    setOpenEditModel(true);
  }

  const handleCloseModel = () => {
    setOpenEditModel(false)
  };

  const [openClientModel, setOpenClientModel] = useState(false);

  const handleOpenClientModel = () => {
    setOpenClientModel(true);
  }

  const handleCloseClientModel = () => {
    setOpenClientModel(false)
  };

  const [openQrModal, setOpenQrModal] = useState(false);

  const handleClientUpdateAfterSubmit = (data) => {
    setUrlDetails(exd => {
      return {
        ...exd,
        ...data
      }
    })
  }

  const { isLoggedIn } = UserLoginData()
  const canManage = Boolean(isLoggedIn?.response && isLoggedIn.response.role !== "client");
  const clicks = Number(detailsOfUrl?.clicks_count) || 0;
  const createdDate = detailsOfUrl?.createdAt ? new Date(detailsOfUrl.createdAt) : null;
  const hasCreatedDate = createdDate && !Number.isNaN(createdDate.getTime());
  const formattedDate = hasCreatedDate
    ? createdDate.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    : "Not available";

  return (
    <SideBar>
      <section className="details-page" aria-labelledby="details-page-title">
        <button type="button" className="details-back" onClick={redirectTo}>
          <FiArrowLeft aria-hidden="true" /> All short links
        </button>
        <header className="shortlinks-page-header details-page-header">
          <span className="create-page-icon" aria-hidden="true"><FiBarChart2 /></span>
          <div>
            <h1 id="details-page-title">Short link details</h1>
            <p>Link overview & engagement</p>
          </div>
        </header>

        {!detailsOfUrl ? (
          <div className="details-loading" role="status">Loading link details...</div>
        ) : (
          <>
            <EditShortenDetails data={detailsOfUrl} open={openEditModel} close={handleCloseModel} />
            <EditClientDetails data={detailsOfUrl} callback={handleClientUpdateAfterSubmit} open={openClientModel} close={handleCloseClientModel} />
            <QrCodeModal
              show={openQrModal}
              onHide={() => setOpenQrModal(false)}
              shortUrl={shortUrl}
              title={detailsOfUrl.title}
              shortCode={detailsOfUrl.short_code || shortCode}
            />
            <Modal show={openDescriptionModal} onHide={() => setOpenDescriptionModal(false)} scrollable aria-labelledby="description-modal-title">
              <Modal.Header closeButton>
                <Modal.Title id="description-modal-title">Full description</Modal.Title>
              </Modal.Header>
              <Modal.Body style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }} dangerouslySetInnerHTML={{ __html: sanitizedDescription }} />
            </Modal>

            <div className="details-overview">
              <div className="details-heading">
                <h2>{detailsOfUrl.title || "Untitled link"}</h2>
                <p style={{ overflowWrap: "anywhere" }}>
                  {descriptionPreview}
                  {hasLongDescription && (
                    <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => setOpenDescriptionModal(true)}>...read more</button>
                  )}
                </p>
                <div className="details-client">
                  <FiUser aria-hidden="true" />
                  <span>{detailsOfUrl.clientName || "Unassigned client"}</span>
                  {canManage && (
                    <button type="button" className="details-icon-button" aria-label="Edit client" title="Edit client" onClick={handleOpenClientModel}><FiEdit2 aria-hidden="true" /></button>
                  )}
                </div>
              </div>
              {canManage && (
                <div className="details-actions">
                  <button type="button" className="details-button" onClick={handleOpenModel}><FiEdit2 aria-hidden="true" /> Edit link</button>
                  <button type="button" className="details-icon-button details-delete" aria-label="Delete link" title="Delete link" onClick={() => {
                    if (window.confirm("Delete this short link? This action cannot be undone.")) {
                      deleteUrlDetails(shortCode);
                      redirectTo();
                    }
                  }}><FiTrash2 aria-hidden="true" /></button>
                </div>
              )}
            </div>

            <div className="details-link-band">
              <div
                className="details-qr-card"
                onClick={() => setOpenQrModal(true)}
                title="Click to customize & download QR code"
                role="button"
                tabIndex={0}
                aria-label="QR code preview"
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setOpenQrModal(true);
                  }
                }}
              >
                {qrDataUrl ? (
                  <img
                    src={qrDataUrl}
                    alt="QR Code preview"
                    width={90}
                    height={90}
                    className="details-qr-image"
                  />
                ) : (
                  <div className="details-qr-placeholder" />
                )}
                <span className="details-qr-hint" aria-hidden="true" title="Customize">
                  <FiMaximize2 />
                </span>
              </div>

              <div className="details-link-content">
                <div className="details-short-link">
                  <span className="details-label">Short link</span>
                  <a href={shortUrl} target="_blank" rel="noopener noreferrer">{shortUrl}<FiArrowUpRight aria-hidden="true" /></a>
                </div>
                <div className="details-link-actions">
                  <button type="button" className="details-button details-copy" onClick={copyToClipboard}>
                    {copiedUrl === shortUrl ? <FiCheck aria-hidden="true" /> : <FiCopy aria-hidden="true" />}
                    <span aria-live="polite">{copiedUrl === shortUrl ? "Copied!" : "Copy link"}</span>
                  </button>
                  <button
                    type="button"
                    className="details-button details-download-qr-button"
                    onClick={() => setOpenQrModal(true)}
                    title="Download QR code (SVG, PNG, JPG, GIF, BMP, WEBP)"
                  >
                    <FiDownload aria-hidden="true" />
                    <span>Download QR</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="details-destination">
              <span className="details-destination-icon" aria-hidden="true"><FiArrowUpRight /></span>
              <div>
                <span className="details-label">Destination URL</span>
                <a href={detailsOfUrl.url} target="_blank" rel="noopener noreferrer">{detailsOfUrl.url}<FiArrowUpRight aria-hidden="true" /></a>
              </div>
            </div>

            <dl className="details-metrics">
              <div className="details-metric details-metric-clicks">
                <span className="details-metric-icon" aria-hidden="true"><FiMousePointer /></span>
                <div><dt>Total clicks</dt><dd>{clicks.toLocaleString()}</dd></div>
              </div>
              <div className="details-metric details-metric-engagement">
                <span className="details-metric-icon" aria-hidden="true"><FiActivity /></span>
                <div><dt>Total engagements</dt><dd>{clicks.toLocaleString()}</dd></div>
              </div>
              <div className="details-metric details-metric-created">
                <span className="details-metric-icon" aria-hidden="true"><FiCalendar /></span>
                <div><dt>Created on</dt><dd><time dateTime={hasCreatedDate ? createdDate.toISOString() : undefined}>{formattedDate}</time></dd></div>
              </div>
            </dl>

            <section className="details-analytics" aria-labelledby="details-analytics-title">
              <header className="details-analytics-header">
                <div><h2 id="details-analytics-title">Engagement over time</h2><p>Daily clicks &middot; All time</p></div>
                <span className="details-chart-legend"><span aria-hidden="true" /> Clicks</span>
              </header>
              <ClickStats initialShortCode={detailsOfUrl.initial_short_code || detailsOfUrl.short_code} />
            </section>
          </>
        )}
      </section>
    </SideBar>
  );
};
